'use client';

import type {
  ProductListSeoLinkListFragmentFragment,
  SeoElementFragmentFragment,
} from '@packages/gql/generated/shopping/graphql';
import { Link } from '@packages/modules/src/Link/Link';
import { Skeleton } from '@packages/shared/src/ui/Skeleton/Skeleton';

import { LazyRender } from '../LazyRender/LazyRender';

const SKELETON_PLACEHOLDER_ITEMS = 5;

export type SeoElementLinkListType =
  | NonNullable<SeoElementFragmentFragment['alternateBrands']>
  | ProductListSeoLinkListFragmentFragment['links'];

const SeoElementLink = ({ link }: { link: SeoElementLinkListType[number] }) => {
  // check link is v2 to allow retrocompatibility with v1
  const isV2 = 'alternateText' in link;
  const alternateText = isV2 ? link.alternateText : link.alt;
  return (
    <div className="col-span-6 md:col-span-4 lg:col-span-4">
      <Link
        className="text-size-body-sm block overflow-hidden no-underline"
        title={alternateText ?? undefined}
        href={link.url!}
        key={link.url}
      >
        {alternateText}
      </Link>
    </div>
  );
};

export type SeoElementLinkListProps = {
  /**
   * A list of seo links with text and url to be aligned in a grid for search-engines.
   */
  linkList: SeoElementLinkListType;
  /**
   * The SEO relevant headline text
   */
  headline: string;
};

/**
 * SeoElementLinkList component renders a single list of search-engine relevant links in a grid.
 * */
export const SeoElementLinkList = ({ linkList, headline }: SeoElementLinkListProps) => (
  <div className="flex flex-col gap-1.5">
    <div className="text-size-headline-2xl text-center md:text-left">{headline}</div>
    <LazyRender
      fallback={
        <div role="group" className="grid grid-cols-12 gap-1.5">
          {Array.from({ length: SKELETON_PLACEHOLDER_ITEMS }).map((_, idx) => (
            <div
              className="col-span-6 md:col-span-4 lg:col-span-4"
              // eslint-disable-next-line @eslint-react/no-array-index-key
              key={`SeoElementLinkListSkeleton${idx}`}
            >
              <Skeleton className="h-1.5 w-full" />
            </div>
          ))}
        </div>
      }
    >
      <div role="group" className="grid grid-cols-12 gap-1.5">
        {linkList.map((link: SeoElementLinkListType[number], index: number) => (
          // in this case we can use index as the key, because the list is static and will not be re-ordered or manipulated during lifecycle (url and alt are not unique!)
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <SeoElementLink link={link} key={index} />
        ))}
      </div>
    </LazyRender>
  </div>
);
